import { addDays, Duration, getHours, intervalToDuration, isFriday, isWeekend, nextMonday, set } from 'date-fns'
import React, { useEffect, useState } from 'react'

import { Skeleton } from '@Components/Skeleton/Skeleton'
import { Trans } from '@Globals/Translations'

import styles from './Countdown.module.scss'

const cutoffHour = 16

function nowInCopenhagen() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))
}

function getNextEndTime() {
  if (typeof window === 'undefined') {
    return { endTime: undefined, context: 'loading' as const }
  }

  const now = nowInCopenhagen()
  const hour = getHours(now)

  // Friday 18:00 to Monday 00:00
  if (isWeekend(now) || (isFriday(now) && hour >= cutoffHour)) {
    const monday = nextMonday(now)
    return { endTime: set(monday, { hours: cutoffHour, minutes: 0, seconds: 0, milliseconds: 0 }), context: 'weekend_cutoff' }
  }

  // Monday to Friday before 10:00
  if (hour < 10) {
    return { endTime: set(now, { hours: cutoffHour, minutes: 0, seconds: 0, milliseconds: 0 }), context: 'weekday_early' }
  }

  // Monday to Friday 10:00 to 16:00
  if (hour < cutoffHour) {
    return { endTime: set(now, { hours: cutoffHour, minutes: 0, seconds: 0, milliseconds: 0 }), context: 'weekday' }
  }

  // Monday to Friday 16:00 to 18:00
  if (hour < 18) {
    const tomorrow = addDays(now, 1)
    return { endTime: set(tomorrow, { hours: cutoffHour, minutes: 0, seconds: 0, milliseconds: 0 }), context: 'weekday_cutoff' }
  }

  // Monday to Friday after 18:00 - count down to 00:00 next day
  const tomorrow = addDays(now, 1)
  return { endTime: set(tomorrow, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }), context: 'weekday_cutoff_late' }
}

function numberLeftPad(input: number | undefined, length = 2) {
  if (!input) {
    return '00'
  }

  return input.toLocaleString('en-US', { minimumIntegerDigits: length, useGrouping: false })
}

const Countdown = ({ end }: { end: Date }) => {
  const [{ hours, minutes, seconds }, setDuration] = useState<Duration>({ hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {}
    }

    function getter() { return intervalToDuration({ start: nowInCopenhagen(), end }) }

    setDuration(getter())

    const timer = setInterval(() => { setDuration(getter()) }, 1000)

    return () => clearInterval(timer)
  }, [end])

  return (
    <strong className={styles.countdown}>
      <span>{numberLeftPad(hours)}</span>
      :
      <span>{numberLeftPad(minutes)}</span>
      :
      <span>{numberLeftPad(seconds)}</span>
    </strong>
  )
}

export const ProductShippingCountdown = () => {
  const [value, setValue] = useState<ReturnType<typeof getNextEndTime>>({ context: 'loading', endTime: undefined })

  useEffect(() => {
    function upsert() {
      setValue((prev) => {
        const next = getNextEndTime()
        if (prev.endTime?.getTime() !== next.endTime?.getTime() || prev.context !== next.context) {
          return next
        }

        return prev
      })
    }

    upsert()

    const timer = setInterval(() => { upsert() }, 1000)

    return () => clearInterval(timer)
  }, [])

  if (value.context === 'loading') {
    return (
      <Skeleton className={styles.skeleton}>&nbsp;</Skeleton>
    )
  }

  return (
    <div>
      <Trans
        components={{
          Countdown: value.endTime ? <Countdown end={value.endTime} /> : <span />,
        }}
        i18nKey="cms:shipping_window"
        values={{ context: value.context }}
      />
    </div>
  )
}
